import { useState, useMemo } from 'react'
import { Plus, Edit2, Trash2, X, Shield, AlertTriangle, CheckCircle } from 'lucide-react'

export default function DataLossPreventionPage() {
  const [dlpPolicies, setDlpPolicies] = useState([
    {
      id: 1,
      name: 'Credit Card Detection',
      description: 'Detect and block credit card numbers in emails',
      riskLevel: 'high',
      enabled: true,
      lastModified: new Date().toLocaleDateString(),
      matchCount: 24
    },
    {
      id: 2,
      name: 'PII Protection',
      description: 'Protect personally identifiable information',
      riskLevel: 'medium',
      enabled: true,
      lastModified: new Date().toLocaleDateString(),
      matchCount: 12
    }
  ])

  const [showModal, setShowModal] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    riskLevel: 'medium',
    patternType: 'regex'
  })

  const handleCreateClick = () => {
    setEditingPolicy(null)
    setFormData({ name: '', description: '', riskLevel: 'medium', patternType: 'regex' })
    setShowModal(true)
  }

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy)
    setFormData({
      name: policy.name,
      description: policy.description,
      riskLevel: policy.riskLevel,
      patternType: 'regex'
    })
    setShowModal(true)
  }

  const handleSavePolicy = () => {
    if (!formData.name || !formData.description) {
      alert('Please fill in all required fields')
      return
    }

    if (editingPolicy) {
      setDlpPolicies(dlpPolicies.map(p =>
        p.id === editingPolicy.id
          ? { ...p, ...formData, lastModified: new Date().toLocaleDateString() }
          : p
      ))
    } else {
      const newPolicy = {
        id: Date.now(),
        ...formData,
        enabled: true,
        lastModified: new Date().toLocaleDateString(),
        matchCount: 0
      }
      setDlpPolicies([...dlpPolicies, newPolicy])
    }

    setShowModal(false)
  }

  const handleDeletePolicy = (id) => {
    if (confirm('Are you sure you want to delete this policy?')) {
      setDlpPolicies(dlpPolicies.filter(p => p.id !== id))
    }
  }

  const handleTogglePolicy = (id) => {
    setDlpPolicies(dlpPolicies.map(p =>
      p.id === id ? { ...p, enabled: !p.enabled } : p
    ))
  }

  const getRiskColor = (level) => {
    switch (level) {
      case 'high': return 'text-red-400'
      case 'medium': return 'text-yellow-400'
      case 'low': return 'text-green-400'
      default: return 'text-blue-400'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl font-bold text-white">Data Loss Prevention</h1>
          </div>
          <p className="text-slate-400">Define and enforce policies to prevent sensitive data exposure</p>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Active Policies</div>
            <div className="text-3xl font-bold text-blue-400">{dlpPolicies.length}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Violations Detected</div>
            <div className="text-3xl font-bold text-yellow-400">{dlpPolicies.reduce((sum, p) => sum + p.matchCount, 0)}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Enabled Policies</div>
            <div className="text-3xl font-bold text-green-400">{dlpPolicies.filter(p => p.enabled).length}</div>
          </div>
        </div>

        {/* Create Button */}
        <button
          onClick={handleCreateClick}
          className="mb-6 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold rounded-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          Create DLP Policy
        </button>

        {/* Policies List */}
        <div className="space-y-4">
          {dlpPolicies.map((policy) => (
            <div
              key={policy.id}
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{policy.name}</h3>
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${getRiskColor(policy.riskLevel)} bg-slate-700`}>
                      {policy.riskLevel.toUpperCase()}
                    </span>
                    {policy.enabled ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                  <p className="text-slate-400 mb-4">{policy.description}</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Violations Detected:</span>
                      <span className="text-white ml-2">{policy.matchCount}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Last Modified:</span>
                      <span className="text-white ml-2">{policy.lastModified}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleTogglePolicy(policy.id)}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      policy.enabled
                        ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
                        : 'bg-red-600/20 text-red-400 hover:bg-red-600/30'
                    }`}
                  >
                    {policy.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                  <button
                    onClick={() => handleEditPolicy(policy)}
                    className="p-2 bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded-lg transition-all"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeletePolicy(policy.id)}
                    className="p-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg transition-all"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingPolicy ? 'Edit DLP Policy' : 'Create DLP Policy'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1 hover:bg-slate-700 rounded-lg transition-all"
                >
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Policy Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    placeholder="e.g., Credit Card Detection"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    placeholder="Describe what this policy detects"
                    rows="3"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Risk Level</label>
                    <select
                      value={formData.riskLevel}
                      onChange={(e) => setFormData({ ...formData, riskLevel: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Pattern Type</label>
                    <select
                      value={formData.patternType}
                      onChange={(e) => setFormData({ ...formData, patternType: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="regex">Regular Expression</option>
                      <option value="keyword">Keyword</option>
                      <option value="fingerprint">Fingerprint</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePolicy}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all"
                >
                  Save Policy
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
